// ============================================================================
// Chat History Service - Frontend API Client
// ============================================================================

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// ============================================================================
// Types & Interfaces
// ============================================================================

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date | string;
  events?: any[];
  recommendedQuestions?: string[];
  isStreaming?: boolean;
  isWaitingForResponse?: boolean;
  tokens?: number;
  isSummary?: boolean;
}

export interface SaveChatRequest {
  chatTitle: string;
  messages: Message[];
  personaId?: number | null;
  personaName?: string;
  totalTokens: number;
}

export interface ChatHistoryItem {
  id: number;
  chatTitle: string;
  chatSummary: string;
  messageCount: number;
  personaName: string | null;
  personaId: number | null;
  totalTokensUsed: number;
  createdAt: string;
  lastAccessedAt: string;
  updatedAt: string;
}

export interface ConversationData {
  id: number;
  chatTitle: string;
  chatSummary: string;
  messages: Message[];
  personaId: number | null;
  personaName: string | null;
  messageCount: number;
  totalTokensUsed: number;
  createdAt: string;
  lastAccessedAt: string;
}

export interface ChatCountResponse {
  success: boolean;
  count: number;
  maxChats: number;
  remaining: number;
}

// ============================================================================
// Chat History Service
// ============================================================================

class ChatHistoryService {
  /**
   * Get auth token from localStorage
   */
  private getAuthToken(): string {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found. Please log in.');
    }
    return token;
  }

  /**
   * Save a new chat conversation
   */
  async saveChat(request: SaveChatRequest): Promise<ChatHistoryItem> {
    try {
      const response = await fetch(`${API_BASE_URL}/chat-history/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        },
        body: JSON.stringify(request)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save chat');
      }

      return data.data;
    } catch (error: any) {
      console.error('Error saving chat:', error);
      throw error;
    }
  }

  /**
   * Get all saved chats for current user
   */
  async getUserChats(): Promise<ChatHistoryItem[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/chat-history/list`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to load chats');
      }

      return data.data;
    } catch (error: any) {
      console.error('Error loading chats:', error);
      throw error;
    }
  }

  /**
   * Load a specific chat by ID
   */
  async loadChat(chatId: number): Promise<ConversationData> {
    try {
      const response = await fetch(`${API_BASE_URL}/chat-history/${chatId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to load chat');
      }

      return data.data;
    } catch (error: any) {
      console.error('Error loading chat:', error);
      throw error;
    }
  }

  /**
   * Delete a saved chat
   */
  async deleteChat(chatId: number): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/chat-history/${chatId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete chat');
      }
    } catch (error: any) {
      console.error('Error deleting chat:', error);
      throw error;
    }
  }

  /**
   * Update chat title (rename)
   */
  async updateChatTitle(chatId: number, newTitle: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/chat-history/${chatId}/title`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        },
        body: JSON.stringify({ newTitle })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to rename chat');
      }
    } catch (error: any) {
      console.error('Error renaming chat:', error);
      throw error;
    }
  }

  /**
   * Get count of saved chats
   */
  async getChatCount(): Promise<ChatCountResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/chat-history/count`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get chat count');
      }

      return data;
    } catch (error: any) {
      console.error('Error getting chat count:', error);
      throw error;
    }
  }

  /**
   * Summarize a conversation, keeping only the last 2 messages
   */
  async summarizeConversation(messages: Message[]): Promise<Message[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/chat-history/summarize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`
        },
        body: JSON.stringify({ messages })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to summarize conversation');
      }

      return data.messages;
    } catch (error: any) {
      console.error('Error summarizing conversation:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const chatHistoryService = new ChatHistoryService();

